/**
 * Vietnamese tone-removal map for slug creation.
 */
const vietnameseMap: Record<string, string> = {
  à: 'a', á: 'a', ạ: 'a', ả: 'a', ã: 'a',
  â: 'a', ầ: 'a', ấ: 'a', ậ: 'a', ẩ: 'a', ẫ: 'a',
  ă: 'a', ằ: 'a', ắ: 'a', ặ: 'a', ẳ: 'a', ẵ: 'a',
  è: 'e', é: 'e', ẹ: 'e', ẻ: 'e', ẽ: 'e',
  ê: 'e', ề: 'e', ế: 'e', ệ: 'e', ể: 'e', ễ: 'e',
  ì: 'i', í: 'i', ị: 'i', ỉ: 'i', ĩ: 'i',
  ò: 'o', ó: 'o', ọ: 'o', ỏ: 'o', õ: 'o',
  ô: 'o', ồ: 'o', ố: 'o', ộ: 'o', ổ: 'o', ỗ: 'o',
  ơ: 'o', ờ: 'o', ớ: 'o', ợ: 'o', ở: 'o', ỡ: 'o',
  ù: 'u', ú: 'u', ụ: 'u', ủ: 'u', ũ: 'u',
  ư: 'u', ừ: 'u', ứ: 'u', ự: 'u', ử: 'u', ữ: 'u',
  ỳ: 'y', ý: 'y', ỵ: 'y', ỷ: 'y', ỹ: 'y',
  đ: 'd',
  À: 'A', Á: 'A', Ạ: 'A', Ả: 'A', Ã: 'A',
  Â: 'A', Ầ: 'A', Ấ: 'A', Ậ: 'A', Ẩ: 'A', Ẫ: 'A',
  Ă: 'A', Ằ: 'A', Ắ: 'A', Ặ: 'A', Ẳ: 'A', Ẵ: 'A',
  È: 'E', É: 'E', Ẹ: 'E', Ẻ: 'E', Ẽ: 'E',
  Ê: 'E', Ề: 'E', Ế: 'E', Ệ: 'E', Ể: 'E', Ễ: 'E',
  Ì: 'I', Í: 'I', Ị: 'I', Ỉ: 'I', Ĩ: 'I',
  Ò: 'O', Ó: 'O', Ọ: 'O', Ỏ: 'O', Õ: 'O',
  Ô: 'O', Ồ: 'O', Ố: 'O', Ộ: 'O', Ổ: 'O', Ỗ: 'O',
  Ơ: 'O', Ờ: 'O', Ớ: 'O', Ợ: 'O', Ở: 'O', Ỡ: 'O',
  Ù: 'U', Ú: 'U', Ụ: 'U', Ủ: 'U', Ũ: 'U',
  Ư: 'U', Ừ: 'U', Ứ: 'U', Ự: 'U', Ử: 'U', Ữ: 'U',
  Ỳ: 'Y', Ý: 'Y', Ỵ: 'Y', Ỷ: 'Y', Ỹ: 'Y',
  Đ: 'D',
};

export function removeVietnameseTones(str: string): string {
  return str
    .split('')
    .map((char) => vietnameseMap[char] || char)
    .join('');
}

/**
 * Create a URL-safe slug from any string.
 * Rules: lowercase, no Vietnamese tones, words separated by single hyphen.
 */
export function createSlug(str: string): string {
  const noTones = removeVietnameseTones(str);
  return noTones
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '') // remove special chars except word, space, hyphen
    .replace(/\s+/g, '-')       // replace spaces with single hyphen
    .replace(/-+/g, '-');       // collapse multiple hyphens
}

/**
 * Create a product slug from product name.
 */
export function productSlug(name: string): string {
  return createSlug(name);
}

/**
 * Create a company slug from company name.
 */
export function companySlug(name: string): string {
  return createSlug(name);
}

/**
 * Build the full product URL: /{company-slug}/{product-slug}
 */
export function getProductUrl(
  product: { companyName?: string; name: string }
): string {
  const cSlug = companySlug(product.companyName ?? "unknown");
  const pSlug = productSlug(product.name);
  return `/${cSlug}/${pSlug}`;
}

/**
 * Build the catalog URL: /chi-tiet/{vendor-slug}/{product-slug}
 */
export function getCatalogUrl(
  product: { companyName?: string; name: string }
): string {
  const cSlug = companySlug(product.companyName ?? "unknown");
  const pSlug = productSlug(product.name);
  return `/chi-tiet/${cSlug}/${pSlug}`;
}

/**
 * Build the flipbook URL: /flipbook/catalog/{vendor-slug}/{product-slug}
 */
export function getFlipbookUrl(
  product: { companyName?: string; name: string }
): string {
  const cSlug = companySlug(product.companyName ?? "unknown");
  const pSlug = productSlug(product.name);
  return `/flipbook/catalog/${cSlug}/${pSlug}`;
}

/**
 * Check if a string looks like a UUID (for backward compat).
 */
export function isUUID(str: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str);
}